import { useState, useEffect, useLayoutEffect, useRef } from 'preact/hooks';
import { t } from './i18n';
import { getConfig } from './config';
import { refreshAuthAndCart } from './api';
import { store } from './store';
import { navigateToView } from './navigation';
import { openContextualLogin, ensureCheckoutReturnTo, completeAuthPageRedirect, isAuthRoutePath } from './returnTo';
import { AuthWidget } from './components/AuthWidget';
import { CartWidget } from './components/CartWidget';
import { CartPage } from './components/CartPage';
import { CurrencyWidget } from './components/CurrencyWidget';
import { LocaleWidget } from './components/LocaleWidget';
import { AccountOverlay, AccountViewContent } from './components/AccountOverlay';
import { ToastContainer } from './components/ToastContainer';
import { SubscribeModalRoot } from './components/SubscribeModalRoot';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CartDrawerRoot } from './components/CartDrawer';
import type { AccountView } from './store';
import { markSubsAuthRedirectNotice } from './subsAuthNotice';
import { DEFAULT_AUTH_DISPLAY_SETTINGS, type AuthDisplaySettings } from './authDisplay';
import { handleReferralCouponEntry } from './referralCouponEntry';

function useStore() {
  const [, setTick] = useState(0);
  useEffect(() => {
    return store.subscribe(() => setTick((n) => n + 1));
  }, []);
  return {
    auth: store.getAuth(),
    cartCount: store.getCartCount(),
    cartTotal: store.getCartTotal(),
    loading: store.getLoading(),
  };
}

// Auth slot only — safe to mount twice (header + menu). Overlays live in AuthChromeRoot / AuthRoot.
export function AuthSlotRoot({
  hideLoginLabel = false,
  displaySettings = DEFAULT_AUTH_DISPLAY_SETTINGS,
}: {
  hideLoginLabel?: boolean;
  displaySettings?: AuthDisplaySettings;
} = {}) {
  const { auth } = useStore();
  const config = getConfig();

  return (
    <ErrorBoundary>
      <AuthWidget
        config={config}
        auth={auth}
        hideLoginLabel={hideLoginLabel}
        displaySettings={displaySettings}
        onAuthChange={async () => {
          await refreshAuthAndCart(config, store);
          await handleReferralCouponEntry(config);
        }}
        onOpenAccountView={(view) => navigateToView(view)}
      />
    </ErrorBoundary>
  );
}

export function AuthChromeRoot() {
  return (
    <ErrorBoundary>
      <AccountOverlay />
      <CartDrawerRoot />
      <SubscribeModalRoot />
      <ToastContainer />
    </ErrorBoundary>
  );
}

// Only Auth — for mount in #shop-auth-widget; SPA overlay for account/orders etc.
export function AuthRoot({
  displaySettings = DEFAULT_AUTH_DISPLAY_SETTINGS,
}: {
  displaySettings?: AuthDisplaySettings;
} = {}) {
  return (
    <ErrorBoundary>
      <AuthSlotRoot displaySettings={displaySettings} />
      <AccountOverlay />
      <CartDrawerRoot />
      <SubscribeModalRoot />
      <ToastContainer />
    </ErrorBoundary>
  );
}

// Only Cart — for mount in #shop-cart-widget
export function CartRoot({ variant, cartType, cartSettings }: { variant: 'mobile' | 'desktop'; cartType?: 'auto' | 'icon' | 'compact' | 'summary'; cartSettings?: import('./config').CartWidgetSettings }) {
  const { cartCount, cartTotal } = useStore();
  return (
    <ErrorBoundary>
      <CartWidget
        count={cartCount}
        total={cartTotal}
        variant={variant}
        type={cartType}
        settings={cartSettings}
        onCartChange={() => {}}
      />
    </ErrorBoundary>
  );
}

// Only Currency — for mount in #shop-currency-widget (notranslate, как в ms2024)
export function CurrencyRoot() {
  return <CurrencyWidget />;
}

// Only Locale — for mount in #shop-locale-widget
export function LocaleRoot() {
  return <LocaleWidget />;
}

// Страница корзины — для #shop-cart-page
export function CartPageRoot() {
  return <CartPage />;
}

const CHECKOUT_INLINE_VIEWS: NonNullable<AccountView>[] = [
  'checkout', 'checkout-qnr', 'checkout-confirm', 'order-success',
  'checkout-auth', 'checkout-login', 'signup',
  'account',
];

const ACCOUNT_PROXY_PATH_TO_VIEW: Record<string, NonNullable<AccountView>> = {
  '/my-account': 'account',
  '/my-account/orders': 'orders',
  '/my-account/tickets': 'tickets',
  '/subscriptions': 'subscriptions',
  '/my-account/subscription': 'subscriptions',
  '/my-account/subscriptions': 'subscriptions',
  '/my-account/bonuses': 'bonus-account',
  '/my-account/bonus-transactions': 'bonus-transactions',
  '/my-account/bonuses/transactions': 'bonus-transactions',
  '/my-account/bonuses/trransactions': 'bonus-transactions',
  '/my-account/referral': 'referral-tree',
  '/my-account/bonuses/referrals': 'referral-tree',
  '/my-account/discounts': 'discounts',
  '/my-account/delete': 'account-delete',
  '/login': 'checkout-login',
  '/signup': 'signup',
  '/my-account/signup': 'signup',
  '/reset-password': 'reset-password',
};

const INLINE_VIEW_CANONICAL_PATHS: Partial<Record<NonNullable<AccountView>, string[]>> = {
  account: ['/my-account'],
  orders: ['/my-account/orders'],
  tickets: ['/my-account/tickets'],
  subscriptions: ['/subscriptions', '/my-account/subscription', '/my-account/subscriptions'],
  'bonus-account': ['/my-account/bonuses'],
  'bonus-transactions': ['/my-account/bonus-transactions', '/my-account/bonuses/transactions', '/my-account/bonuses/trransactions'],
  'referral-tree': ['/my-account/referral', '/my-account/bonuses/referrals'],
  discounts: ['/my-account/discounts'],
  'account-delete': ['/my-account/delete'],
  'checkout-login': ['/login'],
  signup: ['/signup', '/my-account/signup'],
  'reset-password': ['/reset-password'],
};

/** Builder preview: normalized `?path=` or null if not preview. */
function getPreviewNormalizedPath(): string | null {
  if (typeof window === 'undefined' || !window.location.pathname.includes('/preview-proxy')) return null;
  const p = new URLSearchParams(window.location.search).get('path') ?? '/';
  return normalizePreviewPathValue(p);
}

function normalizePreviewPathValue(path: string | null | undefined): string {
  const raw = path || '/';
  try {
    return (new URL(raw, window.location.origin).pathname || '/').replace(/\/+$/, '') || '/';
  } catch {
    return raw.split('?')[0].replace(/\/+$/, '') || '/';
  }
}

/** ShopLight may serve `/subscriptions` or `/my-account/subscriptions`. */
function isSubscriptionsPathNorm(path: string): boolean {
  const n = path.replace(/\/+$/, '') || '/';
  return n === '/my-account/subscriptions' || n === '/subscriptions';
}

/** True when URL is the real subscriptions page (not another path in a multi-placeholder preview shell). */
function isSubscriptionsPagePath(): boolean {
  const pn = getPreviewNormalizedPath();
  if (pn !== null) return isSubscriptionsPathNorm(pn);
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return isSubscriptionsPathNorm(path);
}

/** When current route is login/signup/reset — avoids CheckoutInlineRoot forcing checkout-auth on top of auth pages. */
function detectAuthRouteView(): NonNullable<AccountView> | null {
  const pn = getPreviewNormalizedPath();
  if (pn !== null) {
    const map: Record<string, NonNullable<AccountView>> = {
      '/login': 'checkout-login',
      '/signup': 'signup',
      '/reset-password': 'reset-password',
    };
    return map[pn] ?? null;
  }
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/login' || path.endsWith('/login')) return 'checkout-login';
  if (path === '/signup' || path.endsWith('/signup')) return 'signup';
  if (path.includes('reset-password')) return 'reset-password';
  return null;
}

function detectAccountViewFromProxyPath(): NonNullable<AccountView> | null {
  if (typeof window === 'undefined' || !window.location.pathname.includes('/preview-proxy')) {
    return null;
  }
  const proxyPathParam = new URLSearchParams(window.location.search).get('path') ?? '/';
  const normalized = normalizePreviewPathValue(proxyPathParam);
  return ACCOUNT_PROXY_PATH_TO_VIEW[normalized] ?? null;
}

function inlineViewMatchesPreviewPath(view: NonNullable<AccountView>, path: string | null): boolean {
  if (path === null) return true;
  const normalized = path.replace(/\/+$/, '') || '/';
  const allowed = INLINE_VIEW_CANONICAL_PATHS[view];
  if (!allowed) return true;
  return allowed.some((candidate) => {
    if (normalized === candidate) return true;
    if (view === 'account' || view === 'bonus-account') return false;
    return normalized.startsWith(candidate + '/');
  });
}

/**
 * Inline view for standalone pages (orders, profile, bonuses, etc.).
 * Always renders the designated view — no modal, no accountView gating.
 * Auth-gated: shows login prompt if not authenticated.
 */
export function InlineViewRoot({ view }: { view: NonNullable<AccountView> }) {
  const previewNorm = getPreviewNormalizedPath();
  const hideOffRoutePlaceholder = !inlineViewMatchesPreviewPath(view, previewNorm);
  const hideSubscriptionsPlaceholder =
    previewNorm !== null && view === 'subscriptions' && !isSubscriptionsPathNorm(previewNorm);

  const proxyView = detectAccountViewFromProxyPath();
  const resolvedView: NonNullable<AccountView> = proxyView ?? view;
  const { auth, loading } = useStore();
  const noAuthViews: AccountView[] = ['checkout-login', 'checkout-auth', 'signup', 'forgot-password', 'reset-password'];
  const subscriptionsRedirectRef = useRef(false);

  useLayoutEffect(() => {
    if (loading || hideOffRoutePlaceholder || hideSubscriptionsPlaceholder) return;
    if (auth && (resolvedView === 'checkout-login' || resolvedView === 'signup')) {
      if (typeof window !== 'undefined' && isAuthRoutePath(window.location.pathname)) {
        completeAuthPageRedirect();
      }
      return;
    }
    if (auth) {
      subscriptionsRedirectRef.current = false;
      return;
    }
    if (!isSubscriptionsPagePath()) return;
    if (noAuthViews.includes(resolvedView)) return;
    if (resolvedView !== 'subscriptions') return;
    if (subscriptionsRedirectRef.current) return;
    subscriptionsRedirectRef.current = true;
    markSubsAuthRedirectNotice();
    openContextualLogin('subscriptions');
  }, [loading, auth, resolvedView, hideOffRoutePlaceholder, hideSubscriptionsPlaceholder]);

  if (hideOffRoutePlaceholder || hideSubscriptionsPlaceholder) return null;

  if (
    auth
    && (resolvedView === 'checkout-login' || resolvedView === 'signup')
    && typeof window !== 'undefined'
    && isAuthRoutePath(window.location.pathname)
  ) {
    return <p class="text-muted small text-center py-4 mb-0">{t('inline.redirectingToLogin')}</p>;
  }

  if (loading) {
    return <AccountViewContent view={resolvedView} inline />;
  }
  if (!auth && !noAuthViews.includes(resolvedView)) {
    return (
      <div
        class="d-flex flex-column align-items-center justify-content-center w-100 py-5 px-3"
        style={{ minHeight: 'min(50vh, 440px)', boxSizing: 'border-box' }}
      >
        <div class="sw-surface-card p-4 text-center" style={{ maxWidth: '440px', width: '100%' }}>
          <p class="mb-0 text-muted">
            {t('inline.loginRequiredPrefix')}
            <a href="#" onClick={(e) => { e.preventDefault(); openContextualLogin(resolvedView); }}>{t('inline.loginLink')}</a>
            {t('inline.loginRequiredSuffix')}
          </p>
        </div>
      </div>
    );
  }
  return <AccountViewContent view={resolvedView} inline />;
}

/**
 * Detect which checkout view matches the current page URL via viewUrls config.
 * After a full-page redirect (e.g. checkout → qnr), store.accountView is null;
 * this resolves the correct view from the URL so we don't redirect back.
 */
function detectViewFromUrl(): NonNullable<AccountView> | null {
  if (typeof window === 'undefined') return null;
  const currentPath = window.location.pathname;
  const isPreviewProxy = currentPath.includes('/preview-proxy');
  const proxyPathParam = isPreviewProxy
    ? new URLSearchParams(window.location.search).get('path')
    : null;
  const pathForMatch = isPreviewProxy ? normalizePreviewPathValue(proxyPathParam) : currentPath;
  const orderReturnMatch = pathForMatch.match(/^\/order\/return\/([a-zA-Z0-9_-]+)\/(success|cancel|failure)\/?$/);
  if (orderReturnMatch && document.getElementById('shop-checkout-page')) {
    return 'order-return';
  }
  const { viewUrls } = getConfig();
  if (!viewUrls) return null;
  for (const [viewName, url] of Object.entries(viewUrls)) {
    if (!url) continue;
    try {
      const parsed = new URL(url, window.location.origin);
      const targetPath = isPreviewProxy
        ? normalizePreviewPathValue(parsed.searchParams.get('path') ?? parsed.pathname)
        : parsed.pathname;
      const match = isPreviewProxy
        ? targetPath === pathForMatch
        : targetPath === currentPath;
      if (match && CHECKOUT_INLINE_VIEWS.includes(viewName as NonNullable<AccountView>)) {
        return viewName as NonNullable<AccountView>;
      }
    } catch { /* skip */ }
  }
  return null;
}

/**
 * Inline checkout flow for #shop-checkout-page.
 * Multi-step: auth → payment → qnr → confirm → success.
 * Tracks store.accountView to render the current step.
 */
export function CheckoutInlineRoot() {
  const { auth, loading } = useStore();
  const [view, setView] = useState<AccountView>(store.getAccountView());
  useEffect(() => store.subscribe(() => setView(store.getAccountView())), []);

  useEffect(() => {
    if (loading) return;
    if (!auth) ensureCheckoutReturnTo();
    const current = store.getAccountView();
    if (!current || !CHECKOUT_INLINE_VIEWS.includes(current as NonNullable<AccountView>)) {
      const fromUrl = detectViewFromUrl();
      if (fromUrl) {
        store.setAccountView(fromUrl);
      } else {
        const authRoute = detectAuthRouteView();
        if (authRoute) {
          store.setAccountView(authRoute);
        } else {
          navigateToView(auth ? 'checkout' : 'checkout-auth');
        }
      }
    }
  }, [loading, auth]);

  if (loading) return <span class="shop-widget-loading">…</span>;
  const effectiveView = (view && CHECKOUT_INLINE_VIEWS.includes(view as NonNullable<AccountView>))
    ? view as NonNullable<AccountView>
    : (detectViewFromUrl() || (auth ? 'checkout' : 'checkout-auth'));
  return <AccountViewContent view={effectiveView} />;
}
